import { useForm } from '@sequencehq/utils'
import { required, Validator } from '@sequencehq/utils/validation'
import { ValidatorWithMetadata } from '@sequencehq/utils'
import {
  CountriesAlpha2,
  getStateOptions
} from '@sequencehq/api/utils/commonEnums'
import { useMemo, useState } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useCustomFieldsEditor } from '@sequencehq/core-components'
import {
  CustomerFormSectionValidation,
  CustomerFormFieldsData,
  TaxRegistrationEntry,
  UseCustomerFormFieldsProps
} from './types'
import { getCustomerContactsValidators } from './components/CustomerContactsField'
import { useCustomerNameIsUnique } from './useCustomerNameIsUnique'

/**
 * Form state and validation for the customer form. Rendering is the caller's
 * composition of `CustomerFormFields.*` sections; pass `sectionValidation`
 * mirroring the rendered sections so hidden fields never block submission.
 */
export const useCustomerFormFields = (props: UseCustomerFormFieldsProps) => {
  const {
    initialData,
    onChange,
    onValidationStateChange,
    showValidationErrors = false,
    disableDuplicateNameCheck = false,
    sectionValidation,
    customerId
  } = props
  const validation = resolveSectionValidation(sectionValidation)
  const [formData, setFormData] = useState<CustomerFormFieldsData>(initialData)
  // Picking a country is what says "this customer has an address", so it turns
  // the rest of the address fields into required ones. Leave it blank and the
  // customer saves without an address at all.
  const addressRequired =
    validation.billingInformation && Boolean(formData.address.country)
  // The shipping address works the same way, unless it's the billing address.
  const shippingAddressRequired =
    validation.shippingInformation &&
    !formData.isShippingSameAsBilling &&
    Boolean(formData.shippingAddress.country)
  // With no billing address to share, ticking "Same as billing address" would
  // save no addresses at all, wiping out any shipping address the customer has.
  const sameAsBillingNeedsBillingAddress =
    validation.shippingInformation && !formData.address.country
  const { isPending: isValidatingName, isUnique: isUniqueName } =
    useCustomerNameIsUnique(
      customerId
        ? {
            legalName: formData.legalName,
            isANewCustomer: false,
            customerId,
            enabled: !disableDuplicateNameCheck
          }
        : {
            legalName: formData.legalName,
            isANewCustomer: true,
            enabled: !disableDuplicateNameCheck
          }
    )

  const legalNameValidation = useMemo(() => {
    if (!validation.accountInformation) return []
    const validators: Validator[] = [required]
    if (!disableDuplicateNameCheck) {
      // Reflect the (debounced) uniqueness lookup: pass while it's in flight,
      // then flag a taken name. The lookup already tracks the field value, so
      // the validator ignores its argument.
      validators.push(() => {
        if (isValidatingName) return undefined
        return isUniqueName
          ? undefined
          : 'A customer with this name already exists'
      })
    }
    return validators
  }, [
    validation.accountInformation,
    disableDuplicateNameCheck,
    isValidatingName,
    isUniqueName
  ])

  const contactsValidation = useMemo(
    () =>
      validation.contacts
        ? getCustomerContactsValidators({
            requirePrimaryContact: validation.requirePrimaryContact,
            requireContacts: validation.requireContacts
          })
        : [],
    [
      validation.contacts,
      validation.requirePrimaryContact,
      validation.requireContacts
    ]
  )

  const { fields } = useForm<CustomerFormFieldsData>({
    value: initialData,
    /**
     * Safe to use default (true) because initialData is derived from query data,
     * not from the form's onChange output. No circular dependency risk.
     */
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'legalName',
        validation: legalNameValidation
      },
      {
        property: 'contacts',
        validation: contactsValidation
      },
      {
        // The address as a whole is optional — a customer may have none — so
        // the country is never required. Once a country is picked, though, the
        // rest of the address has to be filled in: `addressRequired` below
        // turns the other validators on.
        property: 'address.country'
      },
      {
        property: 'address.line1',
        validation: addressRequired ? [required] : []
      },
      {
        property: 'address.line2'
      },
      {
        property: 'address.town',
        validation: addressRequired ? [required] : []
      },
      {
        property: 'address.state',
        validation: addressRequired
          ? [requiredIfCountryHasStates('address')]
          : []
      },
      {
        property: 'address.postcode',
        validation: addressRequired ? [required] : []
      },
      {
        property: 'isShippingSameAsBilling',
        validation: sameAsBillingNeedsBillingAddress
          ? [requiresBillingAddress]
          : []
      },
      {
        property: 'shippingAddress.country'
      },
      {
        property: 'shippingAddress.line1',
        validation: shippingAddressRequired ? [required] : []
      },
      {
        property: 'shippingAddress.line2'
      },
      {
        property: 'shippingAddress.town',
        validation: shippingAddressRequired ? [required] : []
      },
      {
        property: 'shippingAddress.state',
        validation: shippingAddressRequired
          ? [requiredIfCountryHasStates('shippingAddress')]
          : []
      },
      {
        property: 'shippingAddress.postcode',
        validation: shippingAddressRequired ? [required] : []
      },
      {
        property: 'taxStatus',
        validation: validation.tax ? [required] : []
      },
      {
        property: 'aliases'
      },
      {
        property: 'label'
      },
      {
        property: 'parentId'
      },
      {
        property: 'taxRegistrations',
        validation: validation.tax
          ? [
              taxRegistrationsHaveCountry,
              taxRegistrationsHaveIdentifier,
              taxRegistrationsHaveStateIfRequired
            ]
          : []
      },
      {
        property: 'customProperties'
      }
    ],
    onChange: (newData: CustomerFormFieldsData) => {
      setFormData(newData)
      onChange?.(newData)
    },
    onValidationStateChange: (isValid: boolean) => {
      onValidationStateChange?.(isValid)
    }
  })

  const customPropertyKeysQuery = useQuery(
    dashboard20240730Client.getCustomerCustomPropertyKeys
  )

  const customPropertyEditor = useCustomFieldsEditor({
    value: fields.customProperties.value,
    onChange: fields.customProperties.onChange,
    availableKeys: customPropertyKeysQuery.data?.items ?? []
  })

  return {
    fields: {
      ...fields,
      legalName: {
        ...fields.legalName,
        isValidating: isValidatingName,
        isUnique: isUniqueName
      }
    },
    customProperties: {
      value: fields.customProperties.value,
      disabled: false,
      ...customPropertyEditor
    }
  }
}

/**
 * Normalises the `sectionValidation` config (booleans or per-section option
 * objects, everything defaulting to enabled) into flat booleans.
 */
const resolveSectionValidation = (config?: CustomerFormSectionValidation) => {
  const contacts = config?.contacts ?? true
  const contactOptions = typeof contacts === 'object' ? contacts : {}

  return {
    accountInformation: config?.accountInformation ?? true,
    contacts: contacts !== false,
    requirePrimaryContact: contactOptions.requirePrimaryContact ?? true,
    requireContacts: contactOptions.requireContacts ?? true,
    billingInformation: config?.billingInformation ?? true,
    shippingInformation: config?.shippingInformation ?? true,
    tax: config?.tax ?? true
  }
}

const requiredIfCountryHasStates =
  (addressKey: 'address' | 'shippingAddress'): Validator =>
  (value: unknown, allValues?: object) => {
    const { country } = (allValues as CustomerFormFieldsData)[addressKey]
    const countryHasStates = country
      ? getStateOptions(country as CountriesAlpha2).length > 0
      : false
    return countryHasStates ? required(value) : undefined
  }

const requiresBillingAddress: Validator = (value: unknown) =>
  value === true ? 'Add a billing address to use it for shipping' : undefined

export const taxRegistrationsHaveCountry: ValidatorWithMetadata<{
  entryId: string
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .map(
      (entry: TaxRegistrationEntry) =>
        !entry.country && {
          message: 'Country is required',
          metadata: { entryId: entry.id, field: 'country' }
        }
    )
    .filter(
      (
        err
      ): err is {
        message: string
        metadata: { entryId: string; field: string }
      } => Boolean(err)
    )
}

export const taxRegistrationsHaveIdentifier: ValidatorWithMetadata<{
  entryId: string
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .map(
      (entry: TaxRegistrationEntry) =>
        !entry.identifier.trim() && {
          message: 'Tax ID is required',
          metadata: { entryId: entry.id, field: 'identifier' }
        }
    )
    .filter(
      (
        err
      ): err is {
        message: string
        metadata: { entryId: string; field: string }
      } => Boolean(err)
    )
}

export const taxRegistrationsHaveStateIfRequired: ValidatorWithMetadata<{
  entryId: string
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry: TaxRegistrationEntry) => {
      const countryHasStates = entry.country
        ? getStateOptions(entry.country as CountriesAlpha2).length > 0
        : false
      return (
        countryHasStates &&
        !entry.state && {
          message: 'State is required',
          metadata: { entryId: entry.id, field: 'state' }
        }
      )
    })
    .filter(
      (
        err
      ): err is {
        message: string
        metadata: { entryId: string; field: string }
      } => Boolean(err)
    )
}
